import { Button, Stack, Box } from "@mui/material";
import { useContext } from "react";
import { ConstantContext } from "components/context/ConstantProvider";
import { Form, FormContext } from "components/shared/Form";
import DictionaryField from "components/ui/atoms/formFields/DictionaryField";
import ListSelectField from "components/ui/atoms/formFields/ListSelectField";
import RHFCheckbox from "components/ui/atoms/formFields/RHFCheckbox";
import RHFDatePicker from "components/ui/atoms/formFields/RHFDatePicker";
import RHFTextField from "components/ui/atoms/formFields/RHFTextField";
import RefinementListField from "components/ui/atoms/formFields/RefinementListField";

const asArray = (obj) => {
  return Array.isArray(obj) ? obj : obj ? [obj] : [];
};

const CompanyFilterFormFields = () => {
  const { dealFlows, dealStatuses, dealInvestmentRounds } =
    useContext(ConstantContext);
  const { onSubmit } = useContext(FormContext);

  return (
    <Stack spacing={2}>
      <ListSelectField
        options={dealFlows
          .sort((a, b) => a.short_name.localeCompare(b.short_name))
          .map((x) => [x.short_name, x.name])}
        multiple
        name="deal_flows"
        label="Dealflow"
      />
      <DictionaryField
        optionsDictionary={Object.fromEntries(
          dealStatuses.map((x) => [x.long, x.long]),
        )}
        multiple
        name="max_investment_status"
        label="Max investment status"
      />
      <Box>
        <RHFTextField
          type="number"
          name="reference_revenue_min"
          label="Min reference revenue"
          placeholder="min"
        />
        <RHFTextField
          sx={{ ml: 2 }}
          type="number"
          name="reference_revenue_max"
          label="Max reference revenue"
          placeholder="max"
        />
      </Box>
      <RefinementListField
        name="sectors"
        refinementListName="sector_tags.value_facet"
        label="Sector tags"
        limit={100}
      />
      <RefinementListField
        name="business_models"
        refinementListName="business_model_tags.value_facet"
        label="Business model"
        limit={100}
      />
      <RefinementListField
        name="mapping_and_certifications"
        refinementListName="mapping_and_certification_tags.value_facet"
        label="Mapping and certification"
        limit={100}
      />
      <RefinementListField
        name="impact_models"
        refinementListName="impact_model_tags.value_facet"
        label="Impact model"
        limit={100}
      />
      <DictionaryField
        optionsDictionary={Object.fromEntries(
          dealStatuses.map((x) => [x.long, x.long]),
        )}
        multiple
        name="investment_status"
        label="Current Investment status"
      />
      <DictionaryField
        optionsDictionary={Object.fromEntries(
          dealInvestmentRounds.map((x) => [x.label, x.label]),
        )}
        multiple
        name="investment_rounds"
        label="Investment round"
      />
      <Box sx={{ display: "flex", justifyContent: "start" }}>
        <RHFCheckbox name="profitable" label="Profitable" nullable />
      </Box>
      <Box>
        <RHFTextField
          type="number"
          name="requested_amount_min"
          label="Min requested amount"
          placeholder="min"
        />
        <RHFTextField
          sx={{ ml: 2 }}
          type="number"
          name="requested_amount_max"
          label="Max requested amount"
          placeholder="max"
        />
      </Box>
      <Box>
        <RHFDatePicker
          type="number"
          name="created_at_min"
          label="Min creation date"
          placeholder="min"
        />
        <RHFDatePicker
          sx={{ ml: 2 }}
          type="number"
          name="created_at_max"
          label="Max creation date"
          placeholder="max"
        />
      </Box>
      <Box>
        <RHFTextField
          type="number"
          name="last_fundraising_amount_min"
          label="Min last fundraising amount"
          placeholder="min"
        />
        <RHFTextField
          sx={{ ml: 2 }}
          type="number"
          name="last_fundraising_amount_max"
          label="Max last fundraising amount"
          placeholder="max"
        />
      </Box>
      <Box>
        <RHFTextField
          type="number"
          name="total_amount_raised_min"
          label="Min total amount raised"
          placeholder="min"
        />
        <RHFTextField
          sx={{ ml: 2 }}
          type="number"
          name="total_amount_raised_max"
          label="Max total amount raised"
          placeholder="max"
        />
      </Box>
      <RefinementListField
        name="country"
        refinementListName="headquarter_address.country_facet"
        label="Country"
        limit={100}
      />
      <RefinementListField
        name="area"
        refinementListName="headquarter_address.administrative_area_level_1_facet"
        label="Area"
        limit={100}
      />
      <RefinementListField
        name="locality"
        refinementListName="headquarter_address.locality_facet"
        label="Locality"
        limit={100}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={onSubmit}>
          Apply
        </Button>
      </Box>
    </Stack>
  );
};

const prepareInitialValues = (object) => ({
  deal_flows: asArray(object?.deal_flows),
  max_investment_status: asArray(object?.max_investment_status),
  reference_revenue_min: object?.reference_revenue_min || "",
  reference_revenue_max: object?.reference_revenue_max || "",
  sectors: asArray(object?.sectors),
  business_models: asArray(object?.business_models),
  mapping_and_certifications: asArray(object?.mapping_and_certifications),
  impact_models: asArray(object?.impact_models),
  investment_status: asArray(object?.investment_status),
  investment_rounds: asArray(object?.investment_rounds),
  profitable:
    object?.profitable === undefined ? null : Boolean(object?.profitable),
  requested_amount_min: object?.requested_amount_min || "",
  requested_amount_max: object?.requested_amount_max || "",
  created_at_min: object?.created_at_min || null,
  created_at_max: object?.created_at_max || null,
  last_fundraising_amount_min: object?.last_fundraising_amount_min || "",
  last_fundraising_amount_max: object?.last_fundraising_amount_max || "",
  total_amount_raised_min: object?.total_amount_raised_min || "",
  total_amount_raised_max: object?.total_amount_raised_max || "",
  country: asArray(object?.country),
  area: asArray(object?.area),
  locality: asArray(object?.locality),
});

const CompanyFilterForm = (props) => {
  return (
    <Form prepareInitialValues={prepareInitialValues} {...props}>
      <CompanyFilterFormFields />
    </Form>
  );
};

export default CompanyFilterForm;
